import { Box, Button, Card, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import ForwardIcon from "@mui/icons-material/Forward";
import { createStyles, makeStyles } from "@mui/material/styles";
import {
	BackdropCircularProgress,
	DownloadFileButton,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	ImportSlipsInstructions,
} from "@websential/cosmic-v20";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
	INITIAL_SNACKBAR_DATA,
	T4_LIST_SCREEN_SLUG,
} from "../../../common/constants";
import { IAppState } from "../../../store/reducers";
import { T4SlipService } from "../../../services/T4SlipService";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";

// import SampleFile from "./Sample_Employee _T4Slips.xlxs"

const instructionList = [
	{ icon: ForwardIcon, text: "Only xml file." },
	{
		icon: ForwardIcon,
		text: "Upload the file for import and press Retrieve Data Button.",
	},
	{ icon: ForwardIcon, text: "Maximum allowed file size is 1MB." },
];
const instructionTitle = "Instructions";
const instructionSubTitle = "File Instructions";


const T4SlipImportXmlLayout = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [selectedFile, setSelectedFile] = useState("");
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const id = useSelector((state: IAppState) => state.client.id);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);

	const handleFileChange = (event: any) => {
		const file = event.target.files[0];
		setSelectedFile(file);
	};

	const handleSubmit = (event: any) => {
		event?.preventDefault();
		const formData = new FormData();
		formData.append("file", selectedFile);

		let payload = {
			id,
			taxYear,
			formData,
		};
		t4SlipRetrieveXmlDataByFile(payload);
	};

	const { isPending: isSubmitting, mutate: t4SlipRetrieveXmlDataByFile } =
		useMutation({
			mutationFn:T4SlipService.t4SlipRetrieveXmlData, 
			onSuccess: (res: any | IServerError | IResponse) => {
				if (res.status === 201) {
				
					setSnackbarData({
						open: true,
						message: res.data.message
							? res.data.message
							: "Data Submitted Successfully!",
						severity: "success",
					});
					setTimeout(() => {
						let path = "/compliance/t4/list";
						navigate(path);
					});
				}
			},
			onError: (err: any) => {
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
			},
		});

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);

	const expandedLabels = ["Compliance", "T4"];
	const expandedT4ListOrListItem = "List";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === "T4" && item.label === expandedT4ListOrListItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: isExpanded,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: T4_LIST_SCREEN_SLUG,
				companyName: clientName,
				activityType: "activity_type=import_xml",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);
	return (
		<>
			<BackdropCircularProgress
				open={isSubmitting}
				testId="import-resource-backdrop"
			/>
			<form onSubmit={handleSubmit}>
				<Box sx={{ width: "100%", marginTop: "2rem" }}>
					<Grid
						container
						rowSpacing={1}
						columnSpacing={{ xs: 1, sm: 2, md: 3 }}
					>
						<Grid size={{ xs: 12, sm:6,  md: 6, lg:6, xl:6 }}>
							<Card>
								<Box sx={{
									fontSize: ".9rem",
									color: "#000",
									padding: "16px 16px 16px 26px",
									fontWeight: "bold",
								}}>
									Import T4 XML SimplePay
								</Box>
								<hr style={{
									borderColor: "rgba(0,0,0,.12)",
									border: "0",
									borderTop: "1px solid rgba(0,0,0,.1)",
									margin: 0,
								}} />
								<Box sx={{ padding: "16px", height: "320px" }}>
									<Grid container>
										<Grid size={{ xs: 12, sm:12,  md: 5, lg:5, xl:5 }} sx={{ p: "12px" }}>
											<Typography sx={{
												color: "rgba(0,0,0,.6)",
												fontSize: ".875rem",
												p: "12px",
											}}>
												Import XML data
											</Typography>
										</Grid>
										<Grid size={{ xs: 12, sm:12,  md: 7, lg:7, xl:7 }} sx={{ p: "12px" }}>
											<Box sx={{ p: "12px" }}>
												<input
													accept=".xml"
													type="file"
													onChange={handleFileChange}
													style={{ color: "rgba(0,0,0,.6)" }}
												/>
												<Typography
													variant="body2"
													sx={{ pt: "8px", color: "rgba(0,0,0,.6)" }}
												>
													Please upload .xml file.
												</Typography>
											</Box>
										</Grid>
									</Grid>
									<Grid container>
										<Grid
											size={{ xs: 12, sm:12,  md: 5, lg:5, xl:5 }}
											sx={{ p: "12px", mt: "20px" }}
										></Grid>
										<Grid
											size={{ xs: 12, sm:12,  md: 7, lg:7, xl:7 }}
											sx={{ p: "12px", mt: "20px" }}
										>
											<Button variant="contained" type="submit">
												Retrieve data
											</Button>
										</Grid>
									</Grid>
								</Box>
							</Card>
						</Grid>
						<Grid size={{ xs: 12, sm:6,  md: 6, lg:6, xl:6 }}>
							<ImportSlipsInstructions
								instructions={instructionList}
								title={instructionTitle}
								subTitle={instructionSubTitle}
								testId="t4-slip-import-instructions"
							/>
						</Grid>
					</Grid>
				</Box>
				<EnhancedSnackbar
					message={snackbarData.message}
					onCloseHandler={onSnackbarClosed}
					severity={snackbarData.severity}
					testId={"resource-list-snackbar"}
					open={snackbarData.open}
				/>
			</form>
		</>
	);
};

export default T4SlipImportXmlLayout;
